import {
  APP_INITIALIZER,
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  InjectionToken,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { buildVersion } from 'src/environments/version';
import { firstValueFrom } from 'rxjs';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', `.json?v=${buildVersion.commit}`);
}

/** Block bootstrap until the active language file is fetched — avoids raw KPI.* keys on first paint. */
export function appInitializerFactory(translate: TranslateService): () => Promise<unknown> {
  return () => {
    const lang = localStorage.getItem('hit2open_lang') || 'en';
    translate.setDefaultLang('en');
    return firstValueFrom(translate.use(lang));
  };
}

import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { StoresComponent } from './pages/stores/stores.component';
import { LayoutComponent } from './layout/layout.component';
import { StoreFormComponent } from './pages/stores/store-form/store-form.component';
import { UserComponent } from './pages/user/user.component';
import { UserVisitComponent } from './pages/user/user-visit/user-visit.component';
import { TermsCondtitionComponent } from './terms-condtition/terms-condtition.component';
import { InterceptorInterceptor } from './interceptor/interceptor.interceptor';
import { AppRoutingModule } from './app-routing.module';
import {
  NbSidebarModule,
  NbLayoutModule,
  NbButtonModule,
  NbInputModule,
  NbMenuModule,
  NbIconModule,
  NbThemeModule,
  NbCardModule,
  NbDialogModule,
  NbSidebarService,
  NbMenuService,
  NbDatepickerModule,
  NbToggleModule,
  NbSelectModule,
  NbTooltipModule,
  NbSpinnerModule,
  NbBadgeModule,
  NbCheckboxModule,
  NbTabsetModule,
} from '@nebular/theme';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { NgxPaginationModule } from 'ngx-pagination';
import { GoogleMapsModule } from '@angular/google-maps';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgSelectModule } from '@ng-select/ng-select';
import { QrPopupComponent } from './pages/qr-popup/qr-popup.component';
import { AdminListComponent } from './pages/admin/admin-list.component';
import { AdminFormComponent } from './pages/admin/admin-form/admin-form.component';
import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { Loader } from '@googlemaps/js-api-loader';
import { AddUserComponent } from './pages/user/add-user/add-user.component';
import { NbDateFnsDateModule } from '@nebular/date-fns';
import { NbMomentDateModule } from '@nebular/moment';
import { ChangePasswordComponent } from './pages/user/change-password/change-password.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OnboardingImagesComponent } from './pages/onboarding-images/onboarding-images.component';
import { AdminChangepasswordComponent } from './pages/admin/admin-changepassword/admin-changepassword.component';
import { GraphComponent } from './graph/graph.component';
import { DoorStatusComponent } from './door-status/door-status.component';
import { NotificationComponent } from './pages/notification/notification.component';
import { QRCodeModule } from 'angularx-qrcode';
import { StoreUrlComponent } from './pages/stores/store-url/store-url.component';
import { StoreOpeningHoursComponent } from './pages/stores/store-opening-hours/store-opening-hours.component';
import { HolidaysComponent } from './pages/holidays/holidays.component';
import { AddHolidaysComponent } from './pages/holidays/add-holidays/add-holidays.component';
import { ApiKeyManagerComponent } from './pages/stores/store-form/api-key-manager/api-key-manager.component';
import { CardUidManagerComponent } from './pages/card-uid-manager/card-uid-manager.component';
import { UserCardLinkComponent } from './pages/card-uid-manager/user-card-link/user-card-link.component';
import { SignaturePadModule } from 'angular2-signaturepad';
import { UserCardsComponent } from './pages/user/user-cards/user-cards.component';
import { RegisterUserComponent } from './pages/card-uid-manager/register-user/register-user.component';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { BlockedCprListComponent } from './pages/blocked-cpr/blocked-cpr-list.component';
import { AddBlockedCprComponent } from './pages/blocked-cpr/add-blocked-cpr.component';
import { UploadBlockedCprComponent } from './pages/blocked-cpr/upload-blocked-cpr.component';
import { NfcMonitoringComponent } from './pages/nfc-monitoring/nfc-monitoring.component';
import { StoreNfcMonitoringComponent } from './pages/nfc-monitoring/store-nfc-monitoring/store-nfc-monitoring.component';

// Ecommerce Admin Components
import { EcommerceAdminComponent } from './pages/stores/ecommerce-admin/ecommerce-admin.component';
import { ProductFormDialogComponent } from './pages/stores/ecommerce-admin/product-management/product-form-dialog/product-form-dialog.component';
import { PlacementHistoryDialogComponent } from './pages/stores/ecommerce-admin/product-management/placement-history-dialog/placement-history-dialog.component';
import { PriceHistoryDialogComponent } from './pages/stores/ecommerce-admin/product-management/price-history-dialog/price-history-dialog.component';
import { AnalyticsDashboardComponent } from './pages/stores/ecommerce-admin/analytics-dashboard/analytics-dashboard.component';
import { ProductManagementComponent } from './pages/stores/ecommerce-admin/product-management/product-management.component';
import { CategoryManagementComponent } from './pages/stores/ecommerce-admin/category-management/category-management.component';
import { CategoryFormDialogComponent } from './pages/stores/ecommerce-admin/category-management/category-form-dialog/category-form-dialog.component';
import { DiscountGroupOffersComponent } from './pages/stores/ecommerce-admin/discount-group-offers/discount-group-offers.component';
import { DiscountGroupOfferFormDialogComponent } from './pages/stores/ecommerce-admin/discount-group-offers/discount-group-offer-form-dialog/discount-group-offer-form-dialog.component';
import { OrderManagementComponent } from './pages/stores/ecommerce-admin/order-management/order-management.component';
import { InventoryDashboardComponent } from './pages/stores/ecommerce-admin/inventory-dashboard/inventory-dashboard.component';
import { AdvancedAnalyticsComponent } from './pages/stores/ecommerce-admin/advanced-analytics/advanced-analytics.component';
import { FinancialDashboardComponent } from './pages/stores/ecommerce-admin/financial-dashboard/financial-dashboard.component';
import { StoreOverviewComponent } from './pages/stores/ecommerce-admin/store-overview/store-overview.component';
import { KpiTilesConfigDialogComponent } from './pages/stores/ecommerce-admin/store-overview/kpi-tiles-config-dialog/kpi-tiles-config-dialog.component';
import { ReportFilterBarComponent } from './shared/components/report-filter-bar/report-filter-bar.component';
import { KpiSavedViewsBarComponent } from './shared/components/kpi-saved-views-bar/kpi-saved-views-bar.component';
import { SaveViewNameDialogComponent } from './shared/components/save-view-name-dialog/save-view-name-dialog.component';
import { StoreConfigComponent } from './pages/stores/ecommerce-admin/store-config/store-config.component';
import { StoreConfigProductsTabComponent } from './pages/stores/ecommerce-admin/store-config/store-config-products-tab/store-config-products-tab.component';
import { ManagerEcommerceComponent } from './pages/stores/manager-ecommerce/manager-ecommerce.component';
import { AuditorOrdersComponent } from './pages/stores/auditor-orders/auditor-orders.component';
import { SecuritySettingsComponent } from './pages/security-settings/security-settings.component';
import { CsvImportComponent } from './pages/stores/csv-import/csv-import.component';
import { CopyStoreDialogComponent } from './pages/stores/copy-store-dialog/copy-store-dialog.component';
import { EcommerceService } from './services/ecommerce.service';
import { CsvImportService } from './services/csv-import.service';

// Chain Management
import { ChainsListComponent } from './pages/chains/chains-list/chains-list.component';
import { ChainFormComponent } from './pages/chains/chain-form/chain-form.component';
import { ChainReportsComponent } from './pages/chains/chain-reports/chain-reports.component';
import { MyChainsDashboardComponent } from './pages/my-chains/my-chains.component';
import { ChainBrandingComponent } from './pages/chains/chain-branding/chain-branding.component';
import { ChainCatalogComponent } from './pages/chains/chain-catalog/chain-catalog.component';
import { ChainLoyaltyComponent } from './pages/chains/chain-loyalty/chain-loyalty.component';
import { ChainOverviewComponent } from './pages/chains/chain-overview/chain-overview.component';

// Developer Clients
import { DeveloperClientsListComponent } from './pages/developer-clients/developer-clients-list/developer-clients-list.component';
import { DeveloperClientFormComponent } from './pages/developer-clients/developer-client-form/developer-client-form.component';
import { SecretDisplayDialogComponent } from './pages/developer-clients/secret-display-dialog/secret-display-dialog.component';

// SSO Applications
import { SsoApplicationsListComponent } from './pages/sso-applications/sso-applications-list/sso-applications-list.component';
import { SsoApplicationFormComponent } from './pages/sso-applications/sso-application-form/sso-application-form.component';

// POS Terminals
import { PosTerminalsComponent } from './pages/stores/pos-terminals/pos-terminals.component';
import { PosTerminalFormComponent } from './pages/stores/pos-terminals/pos-terminal-form.component';

// Loyalty Discount
import { LoyaltyAdminComponent } from './pages/stores/ecommerce-admin/loyalty-admin/loyalty-admin.component';
import { LoyaltyAnalyticsComponent } from './pages/stores/ecommerce-admin/loyalty-analytics/loyalty-analytics.component';

// Basket Audit Trail
import { BasketAuditComponent } from './pages/stores/ecommerce-admin/basket-audit/basket-audit.component';
import { BasketAuditHubComponent } from './pages/stores/ecommerce-admin/basket-audit-hub/basket-audit-hub.component';
import { ScanInsightsComponent } from './pages/stores/ecommerce-admin/scan-insights/scan-insights.component';

// Breece ESL Sync Log
import { BreeceSyncLogComponent } from './pages/stores/ecommerce-admin/breece-sync-log/breece-sync-log.component';

// Terminal Payment Log
import { TerminalLogComponent } from './pages/stores/ecommerce-admin/terminal-log/terminal-log.component';

// POS Client Diagnostics
import { PosDiagnosticsComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/pos-diagnostics.component';
import { PosDiagnosticsListComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/pos-diagnostics-list/pos-diagnostics-list.component';
import { PosDiagnosticsSessionComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/pos-diagnostics-session/pos-diagnostics-session.component';
import { KioskBuildVersionsComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/kiosk-build-versions/kiosk-build-versions.component';
import { WebappUpdateControlComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/webapp-update-control/webapp-update-control.component';
import { TerminalHealthComponent } from './pages/stores/ecommerce-admin/pos-diagnostics/terminal-health/terminal-health.component';

// SKAT Compliance — Electronic Journal & Fiscal Reports
import { ElectronicJournalComponent } from './pages/stores/ecommerce-admin/electronic-journal/electronic-journal.component';
import { FiscalReportsComponent } from './pages/stores/ecommerce-admin/fiscal-reports/fiscal-reports.component';

// Unmatched Barcodes Report
import { UnmatchedBarcodesComponent } from './pages/stores/ecommerce-admin/unmatched-barcodes/unmatched-barcodes.component';

// Support Center
import { SupportCenterComponent } from './pages/support/support-center.component';
import { TicketListComponent } from './pages/support/tickets/ticket-list.component';
import { TicketCreateComponent } from './pages/support/tickets/ticket-create.component';
import { TicketDetailComponent } from './pages/support/tickets/ticket-detail.component';
import { KbListComponent } from './pages/support/knowledge-base/kb-list.component';
import { KbArticleComponent } from './pages/support/knowledge-base/kb-article.component';
import { KbArticleFormComponent } from './pages/support/knowledge-base/kb-article-form.component';
import { KbCategoryManagerComponent } from './pages/support/knowledge-base/kb-category-manager.component';
// Support Center — Phase 3 Calls
import { CallInitiateComponent } from './pages/support/call/call-initiate.component';
import { AdminCallCenterComponent } from './pages/support/call/admin-call-center.component';
import { IncomingCallDialogComponent } from './pages/support/call/incoming-call-dialog.component';
import { CallOverlayComponent } from './pages/support/call/call-overlay.component';
import { CallStatusBadgeComponent } from './pages/support/call/call-status-badge.component';
// Support Center — Phase 4 POS Remote Control
import { PosMonitoringComponent } from './pages/support/pos-monitoring/pos-monitoring.component';
// Support Center — Phase 5 Desktop View
import { DesktopOverlayComponent } from './pages/support/desktop-overlay/desktop-overlay.component';
// Support Center — System Updates
import { SystemUpdatesListComponent } from './pages/support/system-updates/system-updates-list.component';
import { SystemUpdateFormComponent } from './pages/support/system-updates/system-update-form.component';

// Deposit Fee Catalog
import { DepositFeeCatalogComponent } from './pages/deposit-fee-catalog/deposit-fee-catalog.component';

// Global Product Catalog
import { GlobalCatalogComponent } from './pages/global-catalog/global-catalog.component';
import { GlobalProductFormDialogComponent } from './pages/global-catalog/global-product-form-dialog/global-product-form-dialog.component';
import { LinkedProductsDialogComponent } from './pages/global-catalog/linked-products-dialog/linked-products-dialog.component';
import { CsvImportDialogComponent } from './pages/global-catalog/csv-import-dialog/csv-import-dialog.component';
import { ReferenceImportDialogComponent } from './pages/global-catalog/reference-import-dialog/reference-import-dialog.component';
import { GlobalCategoriesTabComponent } from './pages/global-catalog/global-categories-tab/global-categories-tab.component';
import { GlobalCategoryFormDialogComponent } from './pages/global-catalog/global-category-form-dialog/global-category-form-dialog.component';

// Info Page (Signup CMS)
import { InfoPageComponent } from './pages/info-page/info-page.component';

// QR Landing Page CMS
import { QrLandingPageComponent } from './pages/qr-landing-page/qr-landing-page.component';

// Image Library
import { ImageLibraryComponent } from './pages/stores/ecommerce-admin/image-library/image-library.component';

// Invoice imports (wholesaler invoices)
import { InvoiceImportsComponent } from './pages/stores/ecommerce-admin/invoice-imports/invoice-imports.component';
import { InvoiceImportDetailComponent } from './pages/stores/ecommerce-admin/invoice-imports/invoice-import-detail/invoice-import-detail.component';
import { DeliveryGroupDetailComponent } from './pages/stores/ecommerce-admin/invoice-imports/delivery-group-detail/delivery-group-detail.component';
import { InvoiceImportSettingsComponent } from './pages/stores/ecommerce-admin/invoice-imports/invoice-import-settings/invoice-import-settings.component';
import { WholesalerConfigDialogComponent } from './pages/stores/ecommerce-admin/invoice-imports/wholesaler-config-dialog/wholesaler-config-dialog.component';
import { InvoiceCostLayersDialogComponent } from './pages/stores/ecommerce-admin/invoice-imports/invoice-cost-layers-dialog/invoice-cost-layers-dialog.component';
import { InvoiceDraftProductDialogComponent } from './pages/stores/ecommerce-admin/invoice-imports/invoice-draft-product-dialog/invoice-draft-product-dialog.component';
import { SubstitutionMappingDialogComponent } from './pages/stores/ecommerce-admin/invoice-imports/substitution-mapping-dialog/substitution-mapping-dialog.component';
import { InvoiceImportStatusClassPipe } from './pages/stores/ecommerce-admin/invoice-imports/invoice-import-status-class.pipe';
import {
  InvoiceImportLineReviewClassPipe,
  InvoiceImportLineSeverityBadgeClassPipe,
  InvoiceImportLineSeverityClassPipe,
} from './pages/stores/ecommerce-admin/invoice-imports/invoice-import-line-review-class.pipe';
import { UnassignedInvoiceImportsComponent } from './pages/invoice-imports/unassigned-invoice-imports/unassigned-invoice-imports.component';
import { WholesalerDefinitionsAdminComponent } from './pages/invoice-imports/wholesaler-definitions-admin/wholesaler-definitions-admin.component';

// POS Reasons
import { PosReasonsComponent } from './pages/stores/pos-reasons/pos-reasons.component';

// Mobile Diagnostics
import { MobileDiagnosticsComponent } from './pages/mobile-diagnostics/mobile-diagnostics.component';

export const API_KEY = new InjectionToken<string>('apiKey');

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    DashboardComponent,
    StoresComponent,
    StoreFormComponent,
    LayoutComponent,
    UserComponent,
    UserVisitComponent,
    TermsCondtitionComponent,
    QrPopupComponent,
    AdminListComponent,
    AdminFormComponent,
    AddUserComponent,
    ChangePasswordComponent,
    OnboardingImagesComponent,
    AdminChangepasswordComponent,
    GraphComponent,
    DoorStatusComponent,
    NotificationComponent,
    StoreUrlComponent,
    StoreOpeningHoursComponent,
    HolidaysComponent,
    AddHolidaysComponent,
    ApiKeyManagerComponent,
    CardUidManagerComponent,
    UserCardLinkComponent,
    UserCardsComponent,
    RegisterUserComponent,
    BlockedCprListComponent,
    AddBlockedCprComponent,
    UploadBlockedCprComponent,
    NfcMonitoringComponent,
    StoreNfcMonitoringComponent,
    // Ecommerce Admin Components
    EcommerceAdminComponent,
    AnalyticsDashboardComponent,
    ProductManagementComponent,
    ProductFormDialogComponent,
    PlacementHistoryDialogComponent,
    PriceHistoryDialogComponent,
    CategoryManagementComponent,
    CategoryFormDialogComponent,
    DiscountGroupOffersComponent,
    DiscountGroupOfferFormDialogComponent,
    OrderManagementComponent,
    InventoryDashboardComponent,
    AdvancedAnalyticsComponent,
    FinancialDashboardComponent,
    StoreOverviewComponent,
    KpiTilesConfigDialogComponent,
    ReportFilterBarComponent,
    KpiSavedViewsBarComponent,
    SaveViewNameDialogComponent,
    StoreConfigComponent,
    StoreConfigProductsTabComponent,
    ManagerEcommerceComponent,
    AuditorOrdersComponent,
    CsvImportComponent,
    CopyStoreDialogComponent,
    SecuritySettingsComponent,
    // Chain Management
    ChainsListComponent,
    ChainFormComponent,
    ChainReportsComponent,
    MyChainsDashboardComponent,
    ChainBrandingComponent,
    ChainCatalogComponent,
    ChainLoyaltyComponent,
    ChainOverviewComponent,
    // Developer Clients
    DeveloperClientsListComponent,
    DeveloperClientFormComponent,
    SecretDisplayDialogComponent,
    // SSO Applications
    SsoApplicationsListComponent,
    SsoApplicationFormComponent,
    // POS Terminals
    PosTerminalsComponent,
    PosTerminalFormComponent,
    // Loyalty Discount
    LoyaltyAdminComponent,
    LoyaltyAnalyticsComponent,
    // Basket Audit Trail
    BasketAuditComponent,
    BasketAuditHubComponent,
    ScanInsightsComponent,
    // Breece ESL Sync Log
    BreeceSyncLogComponent,
    // Terminal Payment Log
    TerminalLogComponent,
    // POS Client Diagnostics
    PosDiagnosticsComponent,
    PosDiagnosticsListComponent,
    PosDiagnosticsSessionComponent,
    KioskBuildVersionsComponent,
    WebappUpdateControlComponent,
    TerminalHealthComponent,
    // SKAT Compliance
    ElectronicJournalComponent,
    FiscalReportsComponent,
    // Unmatched Barcodes Report
    UnmatchedBarcodesComponent,
    // Support Center
    SupportCenterComponent,
    TicketListComponent,
    TicketCreateComponent,
    TicketDetailComponent,
    KbListComponent,
    KbArticleComponent,
    KbArticleFormComponent,
    KbCategoryManagerComponent,
    // Support Center — Phase 3 Calls
    CallInitiateComponent,
    AdminCallCenterComponent,
    IncomingCallDialogComponent,
    CallOverlayComponent,
    CallStatusBadgeComponent,
    // Support Center — Phase 4 POS Remote Control
    PosMonitoringComponent,
    // Support Center — Phase 5 Desktop View
    DesktopOverlayComponent,
    // Support Center — System Updates
    SystemUpdatesListComponent,
    SystemUpdateFormComponent,
    // Deposit Fee Catalog
    DepositFeeCatalogComponent,
    // Global Product Catalog
    GlobalCatalogComponent,
    GlobalProductFormDialogComponent,
    LinkedProductsDialogComponent,
    CsvImportDialogComponent,
    ReferenceImportDialogComponent,
    GlobalCategoriesTabComponent,
    GlobalCategoryFormDialogComponent,
    // Info Page (Signup CMS)
    InfoPageComponent,
    // QR Landing Page CMS
    QrLandingPageComponent,
    // Image Library
    ImageLibraryComponent,
    // POS Reasons
    PosReasonsComponent,
    // Mobile Diagnostics
    MobileDiagnosticsComponent,
    // Invoice imports
    InvoiceImportsComponent,
    InvoiceImportDetailComponent,
    DeliveryGroupDetailComponent,
    InvoiceImportSettingsComponent,
    WholesalerConfigDialogComponent,
    InvoiceCostLayersDialogComponent,
    InvoiceDraftProductDialogComponent,
    SubstitutionMappingDialogComponent,
    InvoiceImportStatusClassPipe,
    InvoiceImportLineReviewClassPipe,
    InvoiceImportLineSeverityClassPipe,
    InvoiceImportLineSeverityBadgeClassPipe,
    UnassignedInvoiceImportsComponent,
    WholesalerDefinitionsAdminComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NbInputModule,
    NbThemeModule.forRoot(),
    NbLayoutModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    FormsModule,
    FontAwesomeModule,
    NbButtonModule,
    NbSidebarModule.forRoot(),
    NbIconModule,
    NbMenuModule.forRoot(),
    NbEvaIconsModule,
    NbCardModule,
    NgxPaginationModule,
    GoogleMapsModule,
    HttpClientModule,
    AngularEditorModule,
    HttpClientModule,
    NgSelectModule,
    NbDialogModule.forRoot(),
    ToastrModule.forRoot({
      preventDuplicates: true,
      timeOut: 1500,
      positionClass: 'toast-top-right',
    }),
    NgxGpAutocompleteModule,
    NbDatepickerModule.forRoot(),
    NbMomentDateModule,
    NbToggleModule,
    NbSelectModule,
    NbTooltipModule,
    NbSpinnerModule,
    NbBadgeModule,
    NbCheckboxModule,
    NbTabsetModule,
    MatDialogModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    QRCodeModule,
    SignaturePadModule,
    BaseChartDirective,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true,
    },
    NbMenuService,
    NbSidebarService,
    { provide: ToastrService, useClass: ToastrService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InterceptorInterceptor,
      multi: true,
    },
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: 'AIzaSyB64E05RIf1JNgdVAFnhWB-dHUwe7T_m48',
        libraries: ['places'],
      }),
    },
    provideCharts(withDefaultRegisterables()),
    EcommerceService,
    CsvImportService,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
